'use client'

import moment from 'moment'
import { Line, LineChart, XAxis, YAxis } from 'recharts'

import { CurrencyAmount } from '~/components/component-library/CurrencyAmount'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '~/components/ui/chart'

import type { PriceChartPeriod, PricePoint } from '~/hooks/useTokenPriceChart'

export const CHART_HEIGHT_PX = 250

const TICK_FORMATS: Record<PriceChartPeriod, string> = {
  '24h': 'HH:mm',
  '7d': 'MMM D',
  '30d': 'MMM D',
  '1y': 'MMM YYYY',
}

const TOOLTIP_FORMATS: Record<PriceChartPeriod, string> = {
  '24h': 'MMM D, HH:mm',
  '7d': 'MMM D, HH:mm',
  '30d': 'MMM D, YYYY',
  '1y': 'MMM D, YYYY',
}

export const PriceChart = ({
  prices,
  period,
}: {
  prices: PricePoint[]
  period: PriceChartPeriod
}) => {
  const tickFormat = TICK_FORMATS[period]
  const tooltipFormat = TOOLTIP_FORMATS[period]

  return (
    <ChartContainer
      config={{ p: { label: 'Price' } }}
      className="w-full text-blue-500"
      style={{ height: CHART_HEIGHT_PX }}
    >
      <LineChart
        data={prices}
        margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
      >
        <XAxis
          dataKey="t"
          type="number"
          domain={['dataMin', 'dataMax']}
          scale="time"
          tickFormatter={(value: number) => moment(value).format(tickFormat)}
          minTickGap={48}
          tickLine={false}
          axisLine={false}
          tick={{ fontSize: 12, fill: 'var(--color-secondary)' }}
          stroke="currentColor"
          className="text-secondary"
        />
        <YAxis hide domain={['auto', 'auto']} />
        <ChartTooltip
          cursor={{ stroke: 'currentColor', strokeOpacity: 0.2 }}
          content={
            <ChartTooltipContent
              className="text-primary"
              labelFormatter={(_, payload) => {
                const t = payload?.[0]?.payload?.t as number | undefined
                return t ? moment(t).format(tooltipFormat) : ''
              }}
              formatter={(value) => (
                <CurrencyAmount layout="inline" value={value as number} />
              )}
              hideIndicator
            />
          }
        />
        <Line
          dataKey="p"
          type="monotone"
          stroke="currentColor"
          strokeWidth={2}
          dot={false}
          isAnimationActive={false}
        />
      </LineChart>
    </ChartContainer>
  )
}
